import React, { useEffect, useState, useCallback } from 'react';
import useAppStore from './store/appStore';
import QueryPanel from './components/QueryPanel/QueryPanel';
import GraphView from './components/GraphView/GraphView';
import GoalsPanel from './components/GoalsPanel/GoalsPanel';
import InsightsFeed from './components/InsightsFeed/InsightsFeed';
import ReasoningChain from './components/ReasoningChain/ReasoningChain';
import LLMSettings from './components/LLMSettings/LLMSettings';
import Landing from './components/Landing/Landing';
import DevReport from './components/DevReport/DevReport';
import './App.css';

const PIN_KEY = 'asimind_pin_hash';
const SEEN_LANDING_KEY = 'asimind_seen_landing';
const LOGIN_KEY = 'asimind_logged_in';
const DEMO_EMAIL = 'demo@asimind.dev';
const DEMO_PASSWORD = 'demo1234';

function hashPin(pin) {
  // Simple deterministic hash — no server call needed for local-only auth (F06-R09)
  let h = 0;
  for (let i = 0; i < pin.length; i++) {
    h = (Math.imul(31, h) + pin.charCodeAt(i)) | 0;
  }
  return String(h >>> 0);
}

function PinScreen({ onUnlock }) {
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState(localStorage.getItem(PIN_KEY) ? 'enter' : 'set');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (mode === 'set') {
      if (pin.length < 4) { setError('PIN must be at least 4 digits'); return; }
      if (pin !== confirm) { setError('PINs do not match'); return; }
      localStorage.setItem(PIN_KEY, hashPin(pin));
      onUnlock();
    } else {
      if (hashPin(pin) === localStorage.getItem(PIN_KEY)) {
        onUnlock();
      } else {
        setError('Incorrect PIN');
        setPin('');
      }
    }
  }, [mode, pin, confirm, onUnlock]);

  return (
    <div className="pin-screen">
      <div className="pin-card">
        <div className="pin-logo"><span className="logo-asi">ASI:</span><span className="logo-mind">Mind</span></div>
        <div className="pin-tagline">know yourself</div>
        <form onSubmit={handleSubmit} className="pin-form">
          <label className="pin-label">
            {mode === 'set' ? 'Create a local PIN' : 'Enter your PIN'}
          </label>
          <input
            type="password"
            inputMode="numeric"
            maxLength={8}
            value={pin}
            onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setError(''); }}
            placeholder="••••"
            className="pin-input"
            autoFocus
          />
          {mode === 'set' && (
            <input
              type="password"
              inputMode="numeric"
              maxLength={8}
              value={confirm}
              onChange={e => { setConfirm(e.target.value.replace(/\D/g, '')); setError(''); }}
              placeholder="Confirm PIN"
              className="pin-input"
            />
          )}
          {error && <div className="pin-error">{error}</div>}
          <button type="submit" className="pin-submit">
            {mode === 'set' ? 'Set PIN' : 'Unlock'}
          </button>
          {mode === 'enter' && (
            <button type="button" className="pin-reset"
              onClick={() => { localStorage.removeItem(PIN_KEY); setMode('set'); setPin(''); setConfirm(''); setError(''); }}>
              Reset PIN
            </button>
          )}
        </form>
        <div className="pin-note">Your PIN never leaves this device.</div>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      if (email.trim().toLowerCase() === DEMO_EMAIL && password === DEMO_PASSWORD) {
        onLogin();
      } else {
        setError('Incorrect email or password');
        setPassword('');
      }
      setLoading(false);
    }, 400);
  }, [email, password, onLogin]);

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo">
          <span className="logo-asi">ASI:</span><span className="logo-mind">Mind</span>
        </div>
        <div className="login-tagline">know yourself</div>

        <div className="login-demo-badge">Demo Access</div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-field">
            <label className="login-label">Email</label>
            <input
              type="email"
              className="login-input"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              placeholder="demo@asimind.dev"
              autoFocus
              autoComplete="email"
            />
          </div>
          <div className="login-field">
            <label className="login-label">Password</label>
            <input
              type="password"
              className="login-input"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="login-hint">
          <span className="login-hint-label">Demo credentials</span>
          <code className="login-hint-cred">{DEMO_EMAIL}</code>
          <code className="login-hint-cred">demo1234</code>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'chat', label: 'Chat', icon: '💬' },
  { id: 'graph', label: 'Knowledge Graph', icon: '🕸️' },
  { id: 'goals', label: 'Goals', icon: '🎯' },
  { id: 'insights', label: 'Insights', icon: '💡' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
];

export default function App() {
  const [showLanding, setShowLanding] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [unlocked, setUnlocked] = useState(() => !localStorage.getItem(PIN_KEY));
  const activeTab = useAppStore(s => s.activeTab);
  const setActiveTab = useAppStore(s => s.setActiveTab);
  const loadAtoms = useAppStore(s => s.loadAtoms);
  const loadSystemStatus = useAppStore(s => s.loadSystemStatus);
  const atomStats = useAppStore(s => s.atomStats);
  const insights = useAppStore(s => s.insights);
  const [backendOnline, setBackendOnline] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [reportFromLanding, setReportFromLanding] = useState(false);

  useEffect(() => {
    if (!unlocked) return;
    loadSystemStatus()
      .then(() => setBackendOnline(true))
      .catch(() => setBackendOnline(false));
    loadAtoms();
    const refresh = setInterval(() => {
      loadAtoms();
      loadSystemStatus();
    }, 30000);
    return () => clearInterval(refresh);
  }, [unlocked, loadAtoms, loadSystemStatus]);

  if (showLanding) {
    return (
      <>
        <Landing
          onEnter={() => { setShowLanding(false); }}
          onShowReport={() => {
            setReportFromLanding(true);
            setShowReport(true);
          }}
        />
        {showReport && (
          <DevReport
            onClose={() => { setShowReport(false); setReportFromLanding(false); }}
            onBack={reportFromLanding ? () => { setShowReport(false); setReportFromLanding(false); } : undefined}
          />
        )}
      </>
    );
  }

  if (!loggedIn) {
    return <LoginScreen onLogin={() => setLoggedIn(true)} />;
  }

  if (!unlocked) {
    return <PinScreen onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div className="app">
      {/* Mobile topbar */}
      <div className="mobile-topbar">
        <span className="mobile-topbar-logo">
          <span className="logo-asi">ASI:</span><span className="logo-mind">Mind</span>
        </span>
        <div className="mobile-topbar-actions">
          <button className="mobile-topbar-btn" onClick={() => setShowReport(true)}>Dev Report</button>
          <button className="mobile-topbar-btn" onClick={() => setUnlocked(false)}>Lock</button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <span className="logo-asi">ASI:</span>
          <span className="logo-mind">Mind</span>
          <span className="logo-tagline">know yourself</span>
        </div>

        <nav className="sidebar-nav">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'graph') loadAtoms();
              }}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.label}</span>
              {tab.id === 'insights' && insights.length > 0 && (
                <span className="nav-badge">{insights.length}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <SystemStatus status={atomStats} online={backendOnline} />
          <button className="dev-report-button" onClick={() => setShowReport(true)} title="Development Report">
            Dev Report
          </button>
          <button className="lock-button" onClick={() => setUnlocked(false)} title="Lock dashboard">
            Lock
          </button>
        </div>
      </aside>

      {showReport && (
        <DevReport
          onClose={() => { setShowReport(false); setReportFromLanding(false); }}
          onBack={reportFromLanding ? () => { setShowReport(false); setReportFromLanding(false); } : undefined}
        />
      )}

      {/* Main content */}
      <main className="main-content">
        {activeTab === 'chat' && (
          <div className="chat-layout">
            <div className="chat-main">
              <QueryPanel />
            </div>
            <div className="chat-sidebar">
              <ReasoningChain />
            </div>
          </div>
        )}

        {activeTab === 'graph' && (
          <div className="graph-layout">
            <div className="graph-container">
              <GraphView />
            </div>
            <div className="graph-sidebar">
              <ReasoningChain />
            </div>
          </div>
        )}

        {activeTab === 'goals' && <GoalsPanel />}
        {activeTab === 'insights' && <InsightsFeed />}
        {activeTab === 'settings' && <LLMSettings />}
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="mobile-tabbar">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`mobile-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.id); if (tab.id === 'graph') loadAtoms(); }}
          >
            {tab.id === 'insights' && insights.length > 0 && (
              <span className="mobile-tab-badge">{insights.length}</span>
            )}
            <span className="mobile-tab-icon">{tab.icon}</span>
            <span className="mobile-tab-label">{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function SystemStatus({ status, online }) {
  return (
    <div className="system-status">
      <div className={`status-dot ${online === true ? 'online' : online === false ? 'offline' : 'checking'}`} />
      <div className="status-info">
        <div className="status-label">
          {online === true ? 'Backend online' : online === false ? 'Backend offline' : 'Connecting...'}
        </div>
        <div className="status-atoms">
          {status.total_atoms || 0} atoms · {status.das_mode || 'memory'} DAS
        </div>
        {status.llm_available && (
          <div className="status-llm">
            LLM: {status.active_provider === 'gpt-oss' ? 'GPT-OSS 120B' : status.llm_model || 'local'}
          </div>
        )}
      </div>
    </div>
  );
}
